import {
    Mic,
    Rocket,
    Puzzle,
    RefreshCw,
    MessageCircle,
    Heart,
    AlertTriangle,
    Brain,
    Trophy,
    Star,
    Flame,
    FileText,
    ExternalLink,
} from "lucide-react";

import {
    ChecklistStrip,
    CheckList,
    SayThisCard,
    SupportCard,
    WideIntroCard,
} from "../GuideCards";

const PDF_PATH = "/Student_Facilitator_Guide.pdf";

const agreements = [
    "One person speaks at a time",
    "Respect every voice",
    "You can always pass",
    "Confidentiality — what is shared here stays here",
    "Be real. Be kind. Be brave.",
];

const flowTips = [
    "Read the card clearly",
    "Give people a moment to think",
    "Keep things moving — but don’t rush",
];

const upsetTips = [
    "Pause the game if needed",
    "Let them breathe",
    "Keep the space supportive",
];

export default function StudentFacilitatorGuide() {
    return (
        <>
            {/* Hero */}
            <div className="mx-auto mb-10 max-w-4xl text-center">
                <div className="mb-5 inline-flex rotate-[-1deg] items-center gap-2 border-[3px] border-black bg-[#ff3366] px-4 py-2 text-sm font-black uppercase text-white shadow-[4px_4px_0_#000]">
                    <FileText size={18} strokeWidth={3} />
                    Student Facilitator Guide
                </div>

                <h1 className="text-4xl font-black uppercase leading-none tracking-tight md:text-6xl">
                    CALL IT OUT:
                    <span className="block text-[#16bdf4] drop-shadow-[3px_3px_0_#000]">
                        Student Facilitator Guide
                    </span>
                </h1>

                <p className="mx-auto mt-5 max-w-2xl text-lg font-black uppercase leading-8 md:text-xl">
                    Lead the Conversation. Keep It Real. Make It Safe.
                </p>

                <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <a
                        href={PDF_PATH}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-md border-[3px] border-black bg-[#ffbc00] px-5 py-3 text-sm font-black uppercase text-black shadow-[5px_5px_0_#000] transition hover:-translate-y-1 hover:shadow-[7px_7px_0_#000]"
                    >
                        Open PDF Directly
                        <ExternalLink size={18} strokeWidth={3} />
                    </a>

                    <a
                        href="#student-guide-preview"
                        className="inline-flex items-center gap-2 rounded-md border-[3px] border-black bg-white px-5 py-3 text-sm font-black uppercase text-black shadow-[5px_5px_0_#000] transition hover:-translate-y-1 hover:shadow-[7px_7px_0_#000]"
                    >
                        View Embedded Guide
                        <FileText size={18} strokeWidth={3} />
                    </a>
                </div>
            </div>

            {/* Main Guide Content */}
            <div className="mx-auto grid max-w-4xl gap-7">
                <WideIntroCard
                    icon={Mic}
                    title="Your Role"
                    color="bg-[#ffbc00]"
                >
                    <p className="mb-4">
                        You are not here to be perfect. You are here to guide
                        the conversation and help everyone feel safe to speak.
                    </p>

                    <CheckList
                        items={[
                            "Keep it real",
                            "Keep it respectful",
                            "Keep it moving",
                        ]}
                    />
                </WideIntroCard>

                <div className="grid gap-6 md:grid-cols-2">
                    <SayThisCard
                        icon={Rocket}
                        title="Start the Game"
                        color="bg-[#16bdf4]"
                    >
                        Hey everyone—this game is about being real and talking
                        about bullying in a way that actually helps us understand
                        each other. There are no right answers — just real ones.
                    </SayThisCard>

                    <SayThisCard
                        icon={Star}
                        title="End the Game"
                        color="bg-[#16bdf4]"
                    >
                        Before we finish—what is one thing you’re taking from
                        this? Or, is there something you might do differently
                        after today? Thank you for showing up and speaking up!
                        You are appreciated.
                    </SayThisCard>
                </div>

                <ChecklistStrip
                    icon={Puzzle}
                    title="Ground Rules / Agreements"
                    items={agreements}
                    color="bg-[#a855f7]"
                />

                <ChecklistStrip
                    icon={RefreshCw}
                    title="Keep the Game Flowing"
                    items={flowTips}
                    color="bg-[#22c55e]"
                />

                <div className="grid gap-6 md:grid-cols-2">
                    <SayThisCard
                        icon={MessageCircle}
                        title="If the Group Is Quiet"
                        color="bg-[#16bdf4]"
                    >
                        Take a second… anyone want to share? No pressure—just
                        jump in if you feel ready.
                    </SayThisCard>

                    <SayThisCard
                        icon={Heart}
                        title="When Someone Shares"
                        color="bg-[#ff5fa2]"
                    >
                        Thank you for being honest—that takes courage. I
                        appreciate you sharing that.
                    </SayThisCard>
                </div>

                <SupportCard
                    icon={AlertTriangle}
                    title="If Things Get Off Track"
                    color="bg-[#ff6b35]"
                >
                    Let’s bring it back—this is about keeping it real and
                    respectful.
                </SupportCard>

                <SupportCard
                    icon={Brain}
                    title="If Someone Gets Upset"
                    color="bg-[#ffbc00]"
                    tips={upsetTips}
                >
                    You’re good—take your time. You don’t have to share if
                    you don’t want to.
                </SupportCard>

                <section className="rounded-[22px] border-[4px] border-black bg-white p-5 shadow-[7px_7px_0_#000]">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-[3px] border-black bg-[#22c55e] shadow-[4px_4px_0_#000]">
                            <Trophy size={28} strokeWidth={3} />
                        </div>

                        <div>
                            <h2 className="mb-2 text-2xl font-black uppercase">
                                Celebrate the Group
                            </h2>

                            <p className="text-sm font-bold leading-7 md:text-base">
                                Acknowledge the courage it took to show up and participate.
                                Close with a unifying moment like a cheer, clap, or shared
                                gesture.
                            </p>
                        </div>
                    </div>
                </section>
            </div>

            {/* Final Reminder */}
            <div className="mt-8 rounded-[26px] border-[5px] border-black bg-[#fff7cc] p-6 text-center shadow-none md:shadow-[9px_9px_0_#000] md:p-8">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border-[3px] border-black bg-[#ff3366] text-white shadow-[4px_4px_0_#000]">
                    <Flame size={28} strokeWidth={3} />
                </div>

                <h2 className="mb-3 text-3xl font-black uppercase">
                    You Are an Upstander
                </h2>

                <p className="mx-auto max-w-3xl text-base font-bold leading-8 md:text-lg">
                    You don’t have to have all the answers. Just by leading
                    this, you are helping create a space where people feel
                    seen—and that matters more than anything.
                </p>

                <p className="mx-auto mt-5 max-w-3xl text-base font-bold leading-8 md:text-lg">
                    What you do in this circle doesn’t stay here. It carries
                    into your school, your friendships, and your community.
                    Lead with courage. Speak with respect. Make it count.
                </p>
            </div>

            {/* PDF Embed */}
            <section
                id="student-guide-preview"
                className="mt-12 rounded-[26px] border-[5px] border-black bg-white p-4 shadow-none md:shadow-[10px_10px_0_#000] md:p-6"
            >
                <div className="mb-5 flex flex-col gap-4 border-b-[3px] border-black pb-5 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h2 className="text-2xl font-black uppercase">
                            Student Facilitator Guide PDF
                        </h2>
                        <p className="mt-1 text-sm font-bold text-black/70">
                            Preview the guide here, or open the full PDF in a new tab.
                        </p>
                    </div>

                    <a
                        href={PDF_PATH}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-md border-[3px] border-black bg-[#ff3366] px-5 py-3 text-sm font-black uppercase text-white shadow-[5px_5px_0_#000] transition hover:-translate-y-1 hover:shadow-[7px_7px_0_#000]"
                    >
                        Open PDF
                        <ExternalLink size={18} strokeWidth={3} />
                    </a>
                </div>

                <div className="h-[75vh] min-h-[560px] overflow-hidden rounded-[16px] border-[3px] border-black bg-[#e9e9eb]">
                    <iframe
                        src={PDF_PATH}
                        title="CALL IT OUT Student Facilitator Guide PDF"
                        className="h-full w-full"
                    />
                </div>
            </section>
        </>
    );
}